// Types
import { CircuitCategoryLocalized } from '../objects/circuitCategoryLocalized';
import { CircuitCategoryState } from '../objects/circuitCategoryState';
// Action Types
import { Picture } from '../../picture/objects/picture';
import { ADD_CUSTOM_PICTURES, PictureActionTypes } from '../../picture/redux/actionTypes';
import {
    CircuitCategoryActionTypes,
    DELETE_PICTURE,
    MOVE_DOWN_COLUMN,
    MOVE_UP_COLUMN,
    RESET_ADD_CATEGORY,
    RESET_FILTERS,
    RESET_ICON,
    RESIZE_COLUMN,
    SET_CATEGORY,
    SET_CATEGORY_DELETE,
    SET_COLUMNS,
    SET_EDIT_STEP,
    SET_EXPORT_TYPE,
    SET_ICON_ERRORS,
    SET_LIMIT,
    SET_LIST,
    SET_ORDER_BY,
    SET_PAGE,
    SET_RUNNING,
    SET_SEARCH,
    SET_UPLOAD_ICON_VIDEO_INPUT,
    TOGGLE_CANCEL_MODAL,
    TOGGLE_COLUMN,
    TOGGLE_CREATE,
    TOGGLE_EXPORT,
    TOGGLE_EXPORT_FULL,
    TOGGLE_FILTERS,
    TOGGLE_PICTURES_ORDER,
    TOGGLE_REFRESH,
    TOGGLE_UPLOAD_ICON,
    UPDATE_CIRCUIT_CATEGORY_DESCRIPTION_LOCALE,
    UPDATE_CIRCUIT_CATEGORY_NAME_LOCALE,
    UPDATE_FILTER_CREATED_DATE_GTE,
    UPDATE_FILTER_CREATED_DATE_LTE,
    UPDATE_FILTER_CREATOR,
    UPDATE_FILTER_LAST_AUTHOR,
    UPDATE_FILTER_MODIFIED_DATE_GTE,
    UPDATE_FILTER_MODIFIED_DATE_LTE,
    UPDATE_FILTER_NAME,
    UPDATE_FILTER_PERIOD,
    UPDATE_FILTER_PERIOD_TYPE,
    UPDATE_ICON,
    UPDATE_ICON_PROGRESS,
    UPDATE_ICON_STEP,
    UPDATE_PICTURE_ORDER,
    UPDATE_REFERENCE
} from './actionTypes';
import { DEFAULT_CIRCUIT_CATEGORY_COLUMNS } from '../utils/setCircuitCategoryPreference';
import { applyColumnResize } from '../../utils/list/utils/columnResize';

const initialState: CircuitCategoryState = {
    columns: DEFAULT_CIRCUIT_CATEGORY_COLUMNS,
    cancel_modal: false,
    category: null,
    category_delete: null,
    count: 0,
    create: false,
    edit_step: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    circuit_category_name_locales: {},
    circuit_category_description_locales: {},
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_destination: null,
    filter_country: null,
    filter_status: 'ALL',
    filter_name: '',
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period_type: 0,
    filter_period: 'CREATION',
    icon: null,
    icon_errors: null,
    icon_progress: 0,
    limit: 50,
    list: null,
    order_by: '',
    order: 'asc',
    page: 0,
    pictures: [],
    pictures_order: false,
    refresh: false,
    running: false,
    search: '',
    upload_icon: false,
    upload_icon_step: 1,
    upload_icon_video_input: false,
    reference: ''
};

export default (state: CircuitCategoryState = initialState, action: CircuitCategoryActionTypes | PictureActionTypes): CircuitCategoryState => {
    switch (action.type) {
        case ADD_CUSTOM_PICTURES: {
            if (state.create || state.edit_step === 'PICTURES' && action.payload) {
                return { ...state, pictures: [...state.pictures, ...action.payload] };
            }
            return state;
        }
        case DELETE_PICTURE: {
            return { ...state, pictures: state.pictures.reduce((acc:Picture[], picture, index)=> {index !==action.payload && acc.push(picture); return acc;},[] as Picture[]) };
        }
        case MOVE_DOWN_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case MOVE_UP_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case RESET_ADD_CATEGORY: {
            return {
                ...state,
                cancel_modal: false,
                create: false,
                circuit_category_name_locales: {},
                circuit_category_description_locales: {},
                pictures_order: false,
                pictures: [],
                icon: null,
                icon_errors: null,
                icon_progress: 0,
                edit_step: null,
                upload_icon: false,
                upload_icon_step: 1,
                upload_icon_video_input: false,
                category: null,
                reference: ''
            };
        }
        case RESET_FILTERS: {
            return {
                ...state,
                filter_created_date_gte: null,
                filter_created_date_lte: null,
                filter_destination: null,
                filter_country: null,
                filter_status: 'ALL',
                filter_name: '',
                filter_creator: '',
                filter_last_author: '',
                filter_modified_date_gte: null,
                filter_modified_date_lte: null,
                filter_period: 'CREATION',
                filter_period_type: 0
            };
        }
        case SET_CATEGORY: {
            const circuit_category_name_locales: {} & {[index: string]: any} = {};
            const circuit_category_description_locales: {} & {[index: string]: any} = {};
            let has_french = false;
            action.payload.localization.map((locale: CircuitCategoryLocalized) => {
                if (locale.locale === 1) {
                    has_french = true;
                }
                circuit_category_name_locales[locale.locale] = locale.name;
                circuit_category_description_locales[locale.locale] = locale.description;
            });
            if (!has_french) {
                circuit_category_name_locales['1'] = action.payload.name !== null ? action.payload.name : '';
                circuit_category_description_locales['1'] = action.payload.description !== null ? action.payload.description : '';
            }
            return { ...state, category: action.payload, circuit_category_name_locales: circuit_category_name_locales, circuit_category_description_locales, pictures: action.payload.picture?[action.payload.picture]:[], icon: action.payload.icon, reference: action.payload.reference };
        }
        case SET_CATEGORY_DELETE: {
            if (action.payload === null && state.category_delete !== null && state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === state.category_delete.id) {
                        list.splice(i, 1);
                        return { ...state, category_delete: action.payload === false ? null : action.payload, list: list, count: state.count - 1 };
                    }
                }
            }
            return { ...state, category_delete: action.payload === false ? null : action.payload };
        }
        case SET_EDIT_STEP: {
            return { ...state, edit_step: action.payload };
        }
        case SET_EXPORT_TYPE: {
            return { ...state, export_type: action.payload };
        }
        case SET_LIMIT:
            return { ...state, list: null, limit: action.payload };
        case SET_LIST:
            const { count, results } = action.payload;
            console.log(action.payload);
            return { ...state, count: count, list: results, running: false };
        case SET_ORDER_BY: {
            return { ...state, list: null, order_by: action.payload, order: state.order_by === action.payload && state.order === 'asc' ? 'desc': 'asc' };
        }
        case SET_PAGE: {
            return { ...state, list: null, page: action.payload };
        }
        case SET_RUNNING: {
            return { ...state, running: action.payload };
        }
        case SET_SEARCH: {
            return { ...state, list: null, page: 0, search: action.payload };
        }
        case SET_UPLOAD_ICON_VIDEO_INPUT: {
            return { ...state, upload_icon_video_input: action.payload };
        }
        case TOGGLE_CANCEL_MODAL: {
            return { ...state, cancel_modal: !state.cancel_modal };
        }
        case TOGGLE_COLUMN: {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case TOGGLE_PICTURES_ORDER: {
            return { ...state, pictures_order: !state.pictures_order };
        }
        case TOGGLE_CREATE: {
            return { ...state, create: !state.create };
        }
        case TOGGLE_EXPORT: {
            return { ...state, export_list: action.payload };
        }
        case TOGGLE_EXPORT_FULL: {
            return { ...state, export_full: !state.export_full };
        }
        case TOGGLE_FILTERS: {
            return { ...state, filter_open: !state.filter_open };
        }
        case TOGGLE_REFRESH: {
            return { ...state, refresh: !state.refresh };
        }
        case TOGGLE_UPLOAD_ICON: {
            return { ...state, upload_icon: !state.upload_icon, upload_icon_step: 1 };
        }
        case UPDATE_CIRCUIT_CATEGORY_NAME_LOCALE: {
            const { id, value } = action.payload;
            const circuit_category_name_locales = { ...state.circuit_category_name_locales };
            circuit_category_name_locales[id] = value;
            return { ...state, circuit_category_name_locales: circuit_category_name_locales };
        }
        case UPDATE_CIRCUIT_CATEGORY_DESCRIPTION_LOCALE: {
            const { id, value } = action.payload;
            const circuit_category_description_locales = { ...state.circuit_category_description_locales };
            circuit_category_description_locales[id] = value;
            return { ...state, circuit_category_description_locales: circuit_category_description_locales };
        }
        case UPDATE_FILTER_CREATED_DATE_GTE: {
            return { ...state, filter_created_date_gte: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_LTE: {
            return { ...state, filter_created_date_lte: action.payload };
        }
        case UPDATE_FILTER_NAME: {
            return { ...state, filter_name: action.payload };
        }
        case UPDATE_FILTER_CREATOR: {
            return { ...state, filter_creator: action.payload };
        }
        case UPDATE_FILTER_LAST_AUTHOR: {
            return { ...state, filter_last_author: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_GTE: {
            return { ...state, filter_modified_date_gte: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_LTE: {
            return { ...state, filter_modified_date_lte: action.payload };
        }
        case UPDATE_FILTER_PERIOD: {
            const period = action.payload;
            return {
                ...state,
                filter_period: period,
                filter_period_type: 0,
                filter_created_date_gte: period === 'MODIFICATION' ? null : state.filter_created_date_gte,
                filter_created_date_lte: period === 'MODIFICATION' ? null : state.filter_created_date_lte,
                filter_modified_date_gte: period === 'CREATION' ? null : state.filter_modified_date_gte,
                filter_modified_date_lte: period === 'CREATION' ? null : state.filter_modified_date_lte
            };
        }
        case UPDATE_FILTER_PERIOD_TYPE: {
            return { ...state, filter_period_type: action.payload };
        }
        case UPDATE_PICTURE_ORDER: {
            const { drag_index, hover_index } = action.payload;
            const pictures = [...state.pictures];
            pictures.splice(drag_index, 1);
            pictures.splice(hover_index, 0, state.pictures[drag_index]);
            return { ...state, pictures: pictures };
        }
        case UPDATE_ICON: {
            return { ...state, icon_errors: null, upload_icon: false, upload_icon_step: 1, icon_progress: 0, icon: action.payload };
        }
        case UPDATE_ICON_PROGRESS: {
            return { ...state, icon_progress: action.payload };
        }

        case UPDATE_ICON_STEP: {
            return { ...state, upload_icon_step: action.payload };
        }

        case SET_ICON_ERRORS: {
            return { ...state, upload_icon: false, icon_progress: 0, icon_errors: action.payload };
        }
        case RESET_ICON: {
            return { ...state, icon: null };
        }
        case UPDATE_REFERENCE: {
            return { ...state, reference: action.payload };
        }
        case SET_COLUMNS: {
            return { ...state, columns: action.payload };
        }
        case RESIZE_COLUMN: {
            return { ...state, columns: applyColumnResize(state.columns, action.payload) };
        }
        default:
            return state;
    }
};
